// The purchase orders register (`#/purchase-orders`, D71): every numbered PO of a year with its
// approval, delivery and invoicing state. Read-only; each row links to the PO and its request.

import type { DeliveryState } from '../rules/statuses';

/** POs that carry a number: waiting for the CFO, sent first, approved, or rejected. */
export const LISTED_PO_STATUSES = [
  'pendingCFO',
  'pendingVertical',
  'sentPendingCFO',
  'approved',
  'rejected',
] as const;
export type ListedPoStatus = (typeof LISTED_PO_STATUSES)[number];

export interface PoListRow {
  poNo: string;
  prNo: string;
  supplierName: string;
  entity: string;
  vertical: string;
  currency: string;
  /** Minor units, incl. VAT. */
  total: number;
  /** Minor units invoiced so far. */
  invoiced: number;
  status: ListedPoStatus;
  delivery: DeliveryState;
  /** When it went to the CFO (or was sent first). */
  date: string;
  /** The request's title, requester and department (D103); '' when the request is not found. */
  title: string;
  requesterName: string;
  department: string;
}

export interface PoListBody {
  year: number;
  rows: PoListRow[];
}

const DAY = /^\d{4}-\d{2}-\d{2}/;

/** Search and filters, worked out in the browser on the year's rows. */
export function filterPos(
  rows: readonly PoListRow[],
  f: { status: ListedPoStatus | 'all'; entity: string; q: string },
): PoListRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (f.status === 'all' || r.status === f.status) &&
      (!f.entity || r.entity === f.entity) &&
      (!q ||
        r.poNo.toLowerCase().includes(q) ||
        r.prNo.toLowerCase().includes(q) ||
        r.supplierName.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.requesterName.toLowerCase().includes(q) ||
        r.department.toLowerCase().includes(q)),
  );
}

/** Newest first; undated rows last. */
export function sortPos(rows: readonly PoListRow[]): PoListRow[] {
  return [...rows].sort(
    (a, b) =>
      (DAY.test(b.date) ? b.date : '').localeCompare(DAY.test(a.date) ? a.date : '') ||
      b.poNo.localeCompare(a.poNo),
  );
}

/** Money per currency, minor units: SAR, AED and EGP are never added together. */
export type ByCurrency = { currency: string; amount: number }[];

export interface PoSummary {
  /** Value of the POs that count (not rejected), per currency. */
  value: ByCurrency;
  /** Invoiced so far on those, per currency. */
  invoiced: ByCurrency;
  /** With the CFO: waiting for approval or for a signature. */
  withCfo: number;
  /** Out with the supplier and not fully delivered yet. */
  toDeliver: number;
  /** Counted POs (not rejected). */
  count: number;
}

function addTo(list: ByCurrency, currency: string, amount: number) {
  const c = currency || 'SAR';
  const hit = list.find((x) => x.currency === c);
  if (hit) hit.amount += amount;
  else list.push({ currency: c, amount });
}

/** The tiles above the register (D103), for whatever rows are shown. */
export function summarisePos(rows: readonly PoListRow[]): PoSummary {
  const out: PoSummary = { value: [], invoiced: [], withCfo: 0, toDeliver: 0, count: 0 };
  for (const r of rows) {
    if (r.status === 'rejected') continue;
    out.count += 1;
    addTo(out.value, r.currency, r.total);
    addTo(out.invoiced, r.currency, r.invoiced);
    if (
      r.status === 'pendingCFO' ||
      r.status === 'pendingVertical' ||
      r.status === 'sentPendingCFO'
    )
      out.withCfo += 1;
    if (
      (r.status === 'approved' || r.status === 'sentPendingCFO') &&
      (r.delivery === 'none' || r.delivery === 'partial')
    )
      out.toDeliver += 1;
  }
  const big = (a: ByCurrency) => a.sort((x, y) => y.amount - x.amount);
  big(out.value);
  out.invoiced = out.value.map((v) => ({
    currency: v.currency,
    amount: out.invoiced.find((i) => i.currency === v.currency)?.amount ?? 0,
  }));
  return out;
}
